const EMAIL = "contact@tubeoptimizer.net";
const LOCATION = "Wyoming, United States";
const DATE = "September 20, 2026";

const en = {
  nav: [
    { label: "Home", path: "/", testId: "nav-link-home" },
    { label: "About", path: "/about", testId: "nav-link-about" },
    { label: "Apps", path: "/apps", testId: "nav-link-apps" },
    { label: "Contact", path: "/contact", testId: "nav-link-contact" },
  ],
  header: { cta: "Our apps", language: "Language" },
  footer: {
    blurb: "We design and publish mobile applications. Company registered in the State of Wyoming, United States.",
    contact: "Contact",
    legal: "Legal",
    privacy: "Privacy Policy",
    terms: "Terms of Service",
    copyright: "© 2026 TubeOptimizer LLC. All rights reserved.",
    location: "Wyoming · United States",
  },
  marquee: ["Mobile applications", "Simplicity", "Privacy", "User safety", "Quality", "Wyoming, United States"],
  device: { tag1Label: "Principle", tag1Value: "Privacy by default", tag2Label: "Platforms", tag2Value: "iOS · Android" },
  legal: { lastUpdated: "Last updated" },

  home: {
    meta: {
      title: "TubeOptimizer LLC — We design and publish mobile applications",
      description: "TubeOptimizer LLC is a US company (Wyoming, United States) that designs and publishes simple, safe and useful mobile applications.",
    },
    eyebrow: "Mobile app publisher",
    taglineLines: ["We design and publish", "mobile applications."],
    description:
      "TubeOptimizer LLC is a company that builds mobile applications designed to be simple to use, respectful of privacy and genuinely useful in everyday life.",
    ctaApps: "Discover our apps",
    ctaAbout: "Learn more",
    approachEyebrow: "Our approach",
    approachTitle: "Three principles that guide every application we build.",
    chapters: [
      { n: "01", title: "Simplicity", text: "Clear interfaces with nothing superfluous, where every screen has a reason to exist and every gesture a clear purpose." },
      { n: "02", title: "Safety and privacy", text: "Protecting users and their data guides every decision we make, from design through to publication." },
      { n: "03", title: "Usefulness and quality", text: "We only publish applications that are genuinely useful, carefully tested and maintained over time." },
    ],
    ctaSection: {
      eyebrow: "Apps",
      title: "Our first applications are currently in development.",
      text: "They will be announced on the Apps page as soon as they are published.",
      button: "Go to the Apps page",
    },
  },

  about: {
    meta: {
      title: "About — TubeOptimizer LLC",
      description: "TubeOptimizer LLC is a company registered in the United States (Wyoming), founded in 2026, dedicated to building simple, safe and useful mobile applications.",
    },
    eyebrow: "About",
    title: "A US company dedicated to mobile applications.",
    subtitle: "Who we are, what we are building and how we work.",
    who: {
      title: "Who we are",
      text: "TubeOptimizer LLC is a company registered in the United States, in the State of Wyoming, founded in 2026. We develop and publish mobile applications for the general public.",
      facts: [
        { label: "Entity", value: "LLC" },
        { label: "Registered in", value: LOCATION },
        { label: "Founded", value: "2026" },
      ],
    },
    mission: {
      title: "Our mission",
      lead: "Building simple, safe and useful mobile applications.",
      text: "We believe the best applications are the ones that do one thing well, without distracting or complicating. We favour clarity, respect for users and consistent quality over an ever-growing list of features.",
    },
    how: {
      title: "How we work",
      principles: [
        { title: "Privacy", text: "We limit data collection to what is strictly necessary and explain clearly what our applications do." },
        { title: "User safety", text: "We apply proven mobile security practices and fix any identified issue quickly." },
        { title: "Quality", text: "Every application is carefully tested before release, then maintained and improved over time." },
      ],
    },
  },

  apps: {
    meta: {
      title: "Apps — TubeOptimizer LLC",
      description: "Mobile applications published by TubeOptimizer LLC. Our first applications are in development and will be announced here soon.",
    },
    eyebrow: "Apps",
    title: "Our mobile applications.",
    subtitle: "The applications we design and publish will be presented on this page.",
    badge: "In development",
    message: "Our first applications are currently in development and will be announced here soon.",
    placeholder: "Upcoming app ·",
  },

  contact: {
    meta: {
      title: "Contact — TubeOptimizer LLC",
      description: `Contact TubeOptimizer LLC by email at ${EMAIL}. We usually reply within 2 business days.`,
    },
    eyebrow: "Contact",
    title: "Get in touch.",
    subtitle: "For any question about the company or our applications, contact us by email.",
    companyLabel: "Company",
    location: LOCATION,
    emailLabel: "Email",
    responseLabel: "Response time",
    responseTime: "We usually reply within 2 business days.",
  },

  privacy: {
    meta: {
      title: "Privacy Policy — TubeOptimizer LLC",
      description: "Privacy policy for the TubeOptimizer LLC website: visitor data, contact emails, cookies, data security and user rights.",
    },
    eyebrow: "Legal",
    title: "Privacy Policy",
    lastUpdated: DATE,
    sections: [
      {
        id: "introduction",
        title: "Introduction",
        paragraphs: [
          "This privacy policy describes how TubeOptimizer LLC (\"we\") handles information relating to visitors of this website and to people who contact us. It applies to the company website. The mobile applications we publish may have their own privacy policy, available from within the application or its store listing.",
        ],
      },
      {
        id: "visitors",
        title: "Website visitors",
        paragraphs: [
          "This is a static informational website. It has no user accounts, no forms and no database. We do not intentionally collect personal data when you simply browse its pages.",
          "As with any website, the hosting provider may record technical information required to operate and secure the service (IP address, browser type, pages visited, date and time of the request). This information is only used to ensure the availability and security of the site.",
        ],
      },
      {
        id: "emails",
        title: "Contact emails",
        paragraphs: [
          `When you write to us at ${EMAIL}, we receive your email address and the content of your message. This information is used solely to answer your request and follow up on it.`,
          "We keep these exchanges for as long as necessary to handle the request, and then for the period required by any applicable legal obligations. We do not sell or share this information with third parties for commercial purposes.",
        ],
      },
      {
        id: "cookies",
        title: "Cookies",
        paragraphs: [
          "This site does not use advertising cookies and does not set trackers for profiling purposes. The site only stores, in your browser, your language preference (French or English) so it can be offered to you on your next visit. This information stays on your device and is not transmitted to us.",
          "If cookies strictly necessary for the technical operation of the site, or audience measurement tools, were to be used in the future, this policy would be updated accordingly. You can configure your browser at any time to refuse or delete cookies and site data.",
        ],
      },
      {
        id: "security",
        title: "Data security",
        paragraphs: [
          "We implement reasonable technical and organisational measures to protect the information we process against unauthorised access, alteration, disclosure or destruction. The site is served over an encrypted connection (HTTPS).",
        ],
      },
      {
        id: "rights",
        title: "Your rights",
        paragraphs: [
          "Depending on where you live, you may have rights regarding your personal data, including:",
          {
            list: [
              "the right to access the information we hold about you;",
              "the right to request its correction or deletion;",
              "the right to object to processing or request its restriction;",
              "the right to lodge a complaint with a competent supervisory authority.",
            ],
          },
          "To exercise these rights, contact us at the address below. We may ask you to verify your identity before processing your request.",
        ],
      },
      {
        id: "changes",
        title: "Changes",
        paragraphs: [
          "We may update this privacy policy to reflect changes in our practices or legal requirements. The date of the last update is shown at the top of this page. We encourage you to review it regularly.",
        ],
      },
      {
        id: "contact",
        title: "Contact",
        paragraphs: ["For any question about this policy or the handling of your data, you can write to us at:"],
        contact: true,
        address: true,
        location: LOCATION,
      },
    ],
  },

  terms: {
    meta: {
      title: "Terms of Service — TubeOptimizer LLC",
      description: "Terms of service for the TubeOptimizer LLC website: use of the site, intellectual property, limitation of liability and governing law (Wyoming).",
    },
    eyebrow: "Legal",
    title: "Terms of Service",
    lastUpdated: DATE,
    sections: [
      {
        id: "acceptance",
        title: "Acceptance of the terms",
        paragraphs: [
          "By accessing and using this website, you agree to these terms of service. If you do not agree to these terms, please do not use the site. This site is published by TubeOptimizer LLC, a company registered in the State of Wyoming, United States.",
        ],
      },
      {
        id: "use",
        title: "Use of the site",
        paragraphs: [
          "This site is informational: it presents the company and the mobile applications it designs and publishes. You agree to use the site lawfully and not to:",
          {
            list: [
              "interfere with its operation or security;",
              "attempt to access systems or data without authorisation;",
              "reproduce or exploit its content for commercial purposes without prior written consent;",
              "use the site in any way that violates applicable laws.",
            ],
          },
          "Mobile applications published by TubeOptimizer LLC may be subject to their own specific terms of use, available from within the application or its store listing.",
        ],
      },
      {
        id: "intellectual-property",
        title: "Intellectual property",
        paragraphs: [
          "All content on the site (text, names, logos, graphic elements, layout and code) is the property of TubeOptimizer LLC or is used under licence, and is protected by applicable intellectual property laws.",
          "Any reproduction, representation, modification or distribution, in whole or in part, without prior written authorisation is prohibited, except for personal, non-commercial viewing.",
        ],
      },
      {
        id: "liability",
        title: "Limitation of liability",
        paragraphs: [
          "The site is provided \"as is\". We strive to keep the information accurate and up to date, but we do not guarantee the completeness, accuracy or permanent availability of the site.",
          "To the extent permitted by law, TubeOptimizer LLC shall not be liable for any direct or indirect damage resulting from access to the site, its use or the inability to access it. The site may contain links to third-party websites whose content we do not control.",
        ],
      },
      {
        id: "changes",
        title: "Changes",
        paragraphs: [
          "We may modify these terms at any time. The applicable terms are those published on the site at the time of your visit. Continued use of the site after changes are published constitutes acceptance of those changes.",
        ],
      },
      {
        id: "governing-law",
        title: "Governing law",
        paragraphs: [
          "These terms are governed by the laws of the State of Wyoming, United States, without regard to conflict of law principles. Any dispute relating to the use of the site shall fall under the jurisdiction of the courts located in the State of Wyoming, subject to any mandatory provisions that may apply.",
        ],
      },
      {
        id: "contact",
        title: "Contact",
        paragraphs: ["For any question about these terms of service:"],
        contact: true,
        location: LOCATION,
      },
    ],
  },
};

export default en;
